import { Injectable } from '@angular/core';
import { ConfigitAceAssignment, ConfigitAceConfiguration } from '../../../models/configit-ace.model';
import { ConfigitAceService } from '../../../services/configit-ace.service';
import { map } from 'rxjs/operators';
import { AppService } from '../../../services/app.service';
import { ConfigitAceQuestModelTransform } from './ace-quest-model-transform';
import { TranslateService } from '@ngx-translate/core';
import { Quest, QuestionType, QuestPartReset, QuestValueChange } from '@vi/quest';
import { Observable, of } from 'rxjs';
import {
    fromBooleanString,
    fromIsoDate,
    getAssignmentValue,
    isUserAssignment,
    userAssignments,
} from '../../../utils/configit-ace.helper';
import { uniqBy } from 'lodash';
import { Product } from '../../../services/products.service';

@Injectable({
    providedIn: 'root',
})
export class AceQuestAdapterService {
    transformer: ConfigitAceQuestModelTransform;
    constructor(private appService: AppService, translateService: TranslateService, private api: ConfigitAceService) {
        this.transformer = new ConfigitAceQuestModelTransform(translateService);
    }

    public getConfiguration(id: string, existingAssignments: ConfigitAceAssignment[] = []) {
        const salesOrg = this.appService.getSalesOrg();
        const language = this.appService.getLanguage();
        // in configit ace, these variables always have to be set
        const assignments: ConfigitAceAssignment[] = uniqBy(
            [
                {
                    variableId: 'DIM_BUILDDATE',
                    instanceId: 'ROOT',
                    value: new Date().toISOString(),
                },
                {
                    variableId: 'SALESORG',
                    instanceId: 'ROOT',
                    value: salesOrg,
                },
                ...existingAssignments,
            ],
            'variableId'
        );
        return this.api.configure({ productId: id, salesOrg, language, assignments });
    }

    public getModel(id: string, existingAssignments: ConfigitAceAssignment[] = []) {
        return this.getConfiguration(id, existingAssignments).pipe(map((resp) => this.transformer.transform(resp)));
    }

    public check(changed: QuestValueChange): Observable<Quest> {
        const original: ConfigitAceConfiguration = changed.quest.original;
        const value = this.prepareValue(changed);
        const newAssignments = (Array.isArray(value) ? value : [value])
            .filter(Boolean)
            .map((value) => ({ variableId: changed.id, instanceId: changed.question?.instanceId, value }));
        const assignments = this.addAssignment(
            newAssignments,
            userAssignments(original.configuration.configurationAssignments)
        );
        return this.getModel(original.product.id, assignments);
    }

    public reset(reset: QuestPartReset) {
        const original: ConfigitAceConfiguration = reset.quest.original;
        const section = original.sections.find((s) => s.id === reset.part.id);
        const sectionVariables = section.variables.map((v) => v.id);
        const assignments = original.configuration.configurationAssignments.filter(
            (a) => isUserAssignment(a) && !sectionVariables.includes(a.variableId)
        );

        return this.getModel(original.product.id, assignments);
    }

    public getEnergyLabelFromConfiguration(model: Quest, productList: Product[]): Observable<string> {
        const config: ConfigitAceConfiguration = model.original;
        const assignments = config.configuration.configurationAssignments;
        const [relevant, solar, usageSolar] = ['ERP_LABEL_GENERATION', 'ERP_LABEL_SOLAR', 'ERP_LABEL_SOLAR_USAGE'].map(
            (s) => getAssignmentValue(s, assignments)
        );
        if (fromBooleanString(relevant)) {
            const solarUsage = fromBooleanString(solar) ? usageSolar : undefined;
            return this.api.getEnergyLabel({
                salesOrg: this.appService.getSalesOrg(),
                language: this.appService.getLanguage(),
                solarUsage,
                productList,
            });
        }
        return of('energylabelnotrelevant');
    }

    private prepareValue(changed: QuestValueChange) {
        if (changed.question.type === QuestionType.date && changed.value) {
            // configit requires date in local format
            return fromIsoDate(changed.value);
        }
        return changed.value;
    }

    private addAssignment(
        newAssignments: ConfigitAceAssignment[],
        existingAssignments: ConfigitAceAssignment[]
    ): ConfigitAceAssignment[] {
        const assignmentsWithoutNew = existingAssignments.filter(
            (assignment) => !newAssignments.some((a) => a.variableId === assignment.variableId)
        );
        return [...assignmentsWithoutNew, ...newAssignments];
    }
}
